import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { useSubscription } from '../../hooks/useSubscription';
import { FaUser, FaTachometerAlt, FaCreditCard, FaBook, FaPowerOff, FaHome } from 'react-icons/fa';
import { useTranslation } from 'react-i18next';
import './style.css';

const InternalNavbar = () => {
    const { t } = useTranslation();
    const location = useLocation();
    const { user, logout } = useAuth();
    const { hasActiveSubscription, isDemoSubscription, isPaidSubscription } = useSubscription();
    const [isScrolled, setIsScrolled] = useState(false);
    const [showUserMenu, setShowUserMenu] = useState(false);
    const [showMobileMenu, setShowMobileMenu] = useState(false);

    useEffect(() => {
        const handleScroll = () => {
            setIsScrolled(window.scrollY > 20);
        };

        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    const handleLogout = async () => {
        setShowUserMenu(false);
        await logout();
        window.location.href = '/';
    };

    const closeUserMenu = () => {
        setShowUserMenu(false);
    };

    const toggleMobileMenu = () => {
        setShowMobileMenu(!showMobileMenu);
    };

    const getSubscriptionType = () => {
        if (!hasActiveSubscription) return 'none';
        if (isDemoSubscription) return 'demo';
        if (isPaidSubscription) return 'premium';
        return 'none';
    };

    const subscriptionType = getSubscriptionType();

    const isActivePage = (path) => {
        return location.pathname === path;
    };

    return (
        <header className={`internal-navbar ${isScrolled ? 'scrolled' : ''} subscription-${subscriptionType}`}>
            <div className="navbar-container">
                <div className="navbar-content">
                    {/* Left Side Home Button */}
                    <div className="navbar-left">
                        <Link 
                            to="/" 
                            className={`home-button ${isActivePage('/') ? 'active' : ''}`}
                            title={t('backToHome')}
                        >
                            <FaHome />
                            <span>{t('backToHome')}</span>
                        </Link>
                    </div>

                    {/* Navigation Links - Desktop */}
                    <nav className="navbar-nav desktop-nav">
                        <Link 
                            to="/" 
                            className={`nav-link ${isActivePage('/') ? 'active' : ''}`}
                        >
                            <FaHome />
                            <span>{t('home')}</span>
                        </Link>
                        <Link 
                            to="/dashboard" 
                            className={`nav-link ${isActivePage('/dashboard') ? 'active' : ''}`}
                        >
                            <FaTachometerAlt />
                            <span>{t('dashboard')}</span>
                        </Link>
                        <Link 
                            to="/profile" 
                            className={`nav-link ${isActivePage('/profile') ? 'active' : ''}`}
                        >
                            <FaUser />
                            <span>{t('profile')}</span>
                        </Link>
                        <Link 
                            to="/subscription" 
                            className={`nav-link ${isActivePage('/subscription') ? 'active' : ''}`}
                        >
                            <FaCreditCard />
                            <span>{t('subscription')}</span>
                        </Link>
                        <div className="nav-link disabled">
                            <FaBook />
                            <span>{t('courses')}</span>
                            <span className="coming-soon">Soon</span>
                        </div>
                        {user && user.is_admin && (
                            <Link 
                                to="/admin" 
                                className={`nav-link admin-link ${isActivePage('/admin') ? 'active' : ''}`}
                            >
                                <span>⚙️</span>
                                <span>{t('admin')}</span>
                            </Link>
                        )}
                    </nav>

                    {/* User Menu */}
                    {user && (
                        <div className="navbar-user">
                            <div className="user-menu-container">
                                <button 
                                    className="user-menu-trigger"
                                    onClick={() => setShowUserMenu(!showUserMenu)}
                                    aria-label={t('userMenu')}
                                >
                                    {user?.profile_picture ? (
                                        <img 
                                            src={user.profile_picture} 
                                            alt={user.first_name}
                                            className="user-avatar"
                                        />
                                    ) : (
                                        <div className="user-avatar-placeholder">
                                            {user?.first_name?.[0]}{user?.last_name?.[0]}
                                        </div>
                                    )}
                                    <div className="user-info">
                                        <span className="user-name">
                                            {user?.first_name} {user?.last_name}
                                        </span>
                                    </div>
                                    <i className={`ti-angle-${showUserMenu ? 'up' : 'down'} dropdown-arrow`}></i>
                                </button>

                                {/* User Dropdown Menu */}
                                {showUserMenu && (
                                    <>
                                        <div className="user-menu-backdrop" onClick={closeUserMenu}></div>
                                        <div className="user-menu-dropdown">
                                            <div className="dropdown-header">
                                                <div className="dropdown-user-info">
                                                    {user?.profile_picture ? (
                                                        <img 
                                                            src={user.profile_picture} 
                                                            alt={user.first_name}
                                                            className="dropdown-avatar"
                                                        />
                                                    ) : (
                                                        <div className="dropdown-avatar-placeholder">
                                                            {user?.first_name?.[0]}{user?.last_name?.[0]}
                                                        </div>
                                                    )}
                                                    <div className="dropdown-user-details">
                                                        <h4>{user?.first_name} {user?.last_name}</h4>
                                                        <p>{user?.email}</p>
                                                    </div>
                                                </div>
                                            </div>

                                            <div className="dropdown-content">
                                                <Link to="/" onClick={closeUserMenu} className="dropdown-item">
                                                    <FaHome />
                                                    <span>{t('home')}</span>
                                                </Link>
                                                <Link to="/dashboard" onClick={closeUserMenu} className="dropdown-item">
                                                    <FaTachometerAlt />
                                                    <span>{t('dashboard')}</span>
                                                </Link>
                                                <Link to="/profile" onClick={closeUserMenu} className="dropdown-item">
                                                    <FaUser />
                                                    <span>{t('myProfile')}</span>
                                                </Link>
                                                <Link to="/subscription" onClick={closeUserMenu} className="dropdown-item">
                                                    <FaCreditCard />
                                                    <span>{t('subscription')}</span>
                                                </Link>
                                                
                                                {user && user.is_admin && (
                                                    <>
                                                        <div className="dropdown-divider"></div>
                                                        <Link to="/admin" onClick={closeUserMenu} className="dropdown-item admin-item">
                                                            <span>⚙️</span>
                                                            <span>{t('adminDashboard')}</span>
                                                        </Link>
                                                    </>
                                                )}
                                                
                                                <div className="dropdown-divider"></div>
                                                
                                                <div className="dropdown-item disabled">
                                                    <FaBook />
                                                    <span>{t('myCourses')}</span>
                                                    <span className="coming-soon-tag">Soon</span>
                                                </div>

                                                <div className="dropdown-divider"></div>

                                                <button onClick={handleLogout} className="dropdown-item logout-item">
                                                    <FaPowerOff />
                                                    <span>{t('signOut')}</span>
                                                </button>
                                            </div>
                                        </div>
                                    </>
                                )}
                            </div>

                            {/* Mobile Menu Toggle */}
                            <button 
                                className="mobile-menu-toggle"
                                onClick={toggleMobileMenu}
                                aria-label="Toggle mobile menu"
                            >
                                <span className={`hamburger ${showMobileMenu ? 'active' : ''}`}>
                                    <span></span>
                                    <span></span>
                                    <span></span>
                                </span>
                            </button>
                        </div>
                    )}
                </div>

                {/* Mobile Navigation */}
                <div className={`mobile-nav ${showMobileMenu ? 'active' : ''}`}>
                    <div className="mobile-nav-content">
                        <Link 
                            to="/" 
                            className={`mobile-nav-link ${isActivePage('/') ? 'active' : ''}`}
                            onClick={() => setShowMobileMenu(false)}
                        >
                            <FaHome />
                            <span>{t('home')}</span>
                        </Link>
                        <Link 
                            to="/dashboard" 
                            className={`mobile-nav-link ${isActivePage('/dashboard') ? 'active' : ''}`}
                            onClick={() => setShowMobileMenu(false)}
                        >
                            <FaTachometerAlt />
                            <span>{t('dashboard')}</span>
                        </Link>
                        <Link 
                            to="/profile" 
                            className={`mobile-nav-link ${isActivePage('/profile') ? 'active' : ''}`}
                            onClick={() => setShowMobileMenu(false)}
                        >
                            <FaUser />
                            <span>{t('profile')}</span>
                        </Link>
                        <Link 
                            to="/subscription" 
                            className={`mobile-nav-link ${isActivePage('/subscription') ? 'active' : ''}`}
                            onClick={() => setShowMobileMenu(false)}
                        >
                            <FaCreditCard />
                            <span>{t('subscription')}</span>
                        </Link>
                        <div className="mobile-nav-link disabled">
                            <FaBook />
                            <span>{t('courses')}</span>
                            <span className="coming-soon">Soon</span>
                        </div>
                        
                        {user && user.is_admin && (
                            <>
                                <div className="mobile-nav-divider"></div>
                                <Link 
                                    to="/admin" 
                                    className={`mobile-nav-link admin-link ${isActivePage('/admin') ? 'active' : ''}`}
                                    onClick={() => setShowMobileMenu(false)}
                                >
                                    <span>⚙️</span>
                                    <span>{t('admin')}</span>
                                </Link>
                            </>
                        )}
                        
                        <div className="mobile-nav-divider"></div>
                        
                        <button onClick={handleLogout} className="mobile-nav-link logout">
                            <FaPowerOff />
                            <span>{t('signOut')}</span>
                        </button>
                    </div>
                </div>
            </div>
        </header>
    );
};

export default InternalNavbar;